import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SearchIcon, BuildingIcon, HomeIcon, DollarSignIcon, CalculatorIcon, AlertCircleIcon, CheckCircleIcon, TrendingUpIcon, ClockIcon, PercentIcon, ArrowRightIcon, ThumbsUpIcon, ThumbsDownIcon, FileTextIcon, HomeIcon as HomeIconSolid, KeyIcon, AlertTriangleIcon, HistoryIcon, ExternalLinkIcon, GlobeIcon } from 'lucide-react';
import Button from './common/Button';
// Mock data for property search and comps
const mockPropertyData = {
  address: '123 Main Street, Anytown, USA',
  bedrooms: 3,
  bathrooms: 2,
  propertySize: 7500,
  structureSize: 1800,
  yearBuilt: 1985,
  currentZoning: 'Residential',
  currentCondition: 'Fair',
  estimatedValue: 320000,
  zillowEstimate: 325000,
  redfintEstimate: 318000,
  imageUrl: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
};
const mockCompsCurrentCondition = [{
  address: '125 Main Street, Anytown, USA',
  bedrooms: 3,
  bathrooms: 2,
  size: 1750,
  salePrice: 315000,
  saleDate: '2023-02-15',
  distanceInMiles: 0.2
}, {
  address: '118 Oak Avenue, Anytown, USA',
  bedrooms: 3,
  bathrooms: 2.5,
  size: 1900,
  salePrice: 335000,
  saleDate: '2023-03-10',
  distanceInMiles: 0.5
}, {
  address: '87 Pine Street, Anytown, USA',
  bedrooms: 3,
  bathrooms: 2,
  size: 1780,
  salePrice: 310000,
  saleDate: '2023-01-20',
  distanceInMiles: 0.7
}];
const mockCompsBestUse = [{
  address: '142 Maple Drive, Anytown, USA',
  bedrooms: 4,
  bathrooms: 3,
  size: 2200,
  salePrice: 450000,
  saleDate: '2023-03-05',
  distanceInMiles: 0.4,
  notes: 'Recently renovated'
}, {
  address: '156 Elm Street, Anytown, USA',
  bedrooms: 4,
  bathrooms: 2.5,
  size: 2100,
  salePrice: 425000,
  saleDate: '2023-02-28',
  distanceInMiles: 0.6,
  notes: 'Added master suite'
}, {
  address: '110 Cedar Lane, Anytown, USA',
  bedrooms: 3,
  bathrooms: 3,
  size: 2000,
  salePrice: 410000,
  saleDate: '2023-01-15',
  distanceInMiles: 0.5,
  notes: 'Modern renovation'
}];
const mockRecommendations = [{
  type: 'Full Renovation',
  description: 'Complete interior renovation with modern finishes',
  estimatedCost: 85000,
  estimatedValue: 420000,
  estimatedROI: 17.6,
  timeframe: '4-6 months'
}, {
  type: 'Kitchen & Bath Update',
  description: 'Focus on kitchen and bathrooms with light cosmetic updates elsewhere',
  estimatedCost: 45000,
  estimatedValue: 380000,
  estimatedROI: 13.3,
  timeframe: '2-3 months'
}, {
  type: 'ADU Addition',
  description: 'Add a 600 sq ft ADU in the backyard for rental income',
  estimatedCost: 120000,
  estimatedValue: 480000,
  estimatedROI: 33.3,
  timeframe: '6-8 months'
}];
// Deal status types
const DEAL_STATUS = {
  ANALYSIS: 'analysis',
  PASSED: 'passed',
  OFFER: 'offer',
  ESCROW: 'escrow',
  PORTFOLIO: 'portfolio'
};
// Helper functions to generate Zillow and Redfin URLs
const getZillowUrl = address => {
  // Format address for URL (replace spaces with -, remove commas, etc.)
  const formattedAddress = address.replace(/\s+/g, '-').replace(/,/g, '').toLowerCase();
  return `https://www.zillow.com/homes/${formattedAddress}_rb/`;
};
const getRedfinUrl = address => {
  // Format address for URL (replace spaces with +, keep commas)
  const formattedAddress = address.replace(/\s+/g, '+');
  return `https://www.redfin.com/search?q=${formattedAddress}`;
};
const DealInput = () => {
  // State for address input
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [propertyData, setPropertyData] = useState(null);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  // Deal status tracking
  const [dealStatus, setDealStatus] = useState(DEAL_STATUS.ANALYSIS);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  // Financial calculation states
  const [purchasePrice, setPurchasePrice] = useState('');
  const [constructionCost, setConstructionCost] = useState('');
  const [holdingPeriod, setHoldingPeriod] = useState('6');
  const [holdingCostPerMonth, setHoldingCostPerMonth] = useState('2000');
  const [salesCommissionPercent, setSalesCommissionPercent] = useState('5');
  const [afterRepairValue, setAfterRepairValue] = useState('');
  // Add state for saving deals
  const [savedToHistory, setSavedToHistory] = useState(false);
  // State for valuation source
  const [valuationSource, setValuationSource] = useState('manual');
  const navigate = useNavigate();
  const location = useLocation();
  // Check if we're editing an existing deal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dealId = params.get('id');
    if (dealId) {
      // In a real app, we would fetch the deal from the server
      // For now, we'll check localStorage
      const savedDeals = localStorage.getItem('dealHistory');
      if (savedDeals) {
        const deals = JSON.parse(savedDeals);
        const deal = deals.find(d => d.id === dealId);
        if (deal) {
          setAddress(deal.address);
          setPropertyData({
            address: deal.address,
            bedrooms: 3,
            bathrooms: 2,
            propertySize: 7500,
            structureSize: 1800,
            yearBuilt: 1985,
            currentZoning: 'Residential',
            currentCondition: 'Fair',
            estimatedValue: deal.purchasePrice,
            zillowEstimate: deal.purchasePrice * 1.02,
            redfintEstimate: deal.purchasePrice * 0.98,
            imageUrl: deal.imageUrl
          });
          setPurchasePrice(deal.purchasePrice.toString());
          setAfterRepairValue(deal.arv.toString());
          setConstructionCost(deal.constructionCost.toString());
          setDealStatus(deal.status);
          setSavedToHistory(true);
        }
      }
    }
  }, [location]);
  // Mock AI property search
  const handlePropertySearch = () => {
    if (!address.trim()) return;
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      setPropertyData(mockPropertyData);
      setIsLoading(false);
      // Set initial values for financial calculations
      setPurchasePrice('320000');
      setAfterRepairValue(mockRecommendations[0].estimatedValue.toString());
      setConstructionCost(mockRecommendations[0].estimatedCost.toString());
      setSelectedRecommendation(mockRecommendations[0]);
      // Reset deal status for new property
      setDealStatus(DEAL_STATUS.ANALYSIS);
    }, 1500);
  };
  const selectRecommendation = recommendation => {
    setSelectedRecommendation(recommendation);
    setConstructionCost(recommendation.estimatedCost.toString());
    setAfterRepairValue(recommendation.estimatedValue.toString());
  };
  // Handle valuation source change
  const handleValuationSourceChange = source => {
    setValuationSource(source);
    if (propertyData) {
      if (source === 'zillow') {
        setPurchasePrice(propertyData.zillowEstimate.toString());
      } else if (source === 'redfin') {
        setPurchasePrice(propertyData.redfintEstimate.toString());
      } else if (source === 'manual') {
        setPurchasePrice(propertyData.estimatedValue.toString());
      }
    }
  };
  // Calculate financial metrics
  const calculateFinancials = () => {
    const purchase = parseFloat(purchasePrice) || 0;
    const construction = parseFloat(constructionCost) || 0;
    const holding = parseFloat(holdingCostPerMonth) * parseFloat(holdingPeriod) || 0;
    const arv = parseFloat(afterRepairValue) || 0;
    const salesCommission = arv * parseFloat(salesCommissionPercent) / 100 || 0;
    const totalCost = purchase + construction + holding + salesCommission;
    const grossProfit = arv - purchase;
    const netProfit = arv - totalCost;
    const roi = netProfit > 0 ? netProfit / totalCost * 100 : 0;
    return {
      totalInvestment: totalCost,
      grossProfit,
      netProfit,
      roi
    };
  };
  // Deal workflow functions
  const handleAcceptDeal = () => {
    setDealStatus(DEAL_STATUS.OFFER);
  };
  const handleRejectDeal = () => {
    setShowRejectionModal(true);
  };
  const confirmRejectDeal = () => {
    setDealStatus(DEAL_STATUS.PASSED);
    setShowRejectionModal(false);
  };
  const handleMoveToEscrow = () => {
    setDealStatus(DEAL_STATUS.ESCROW);
  };
  const handleMoveToPortfolio = () => {
    setDealStatus(DEAL_STATUS.PORTFOLIO);
  };
  const resetDealStatus = () => {
    setDealStatus(DEAL_STATUS.ANALYSIS);
    setRejectionReason('');
  };
  const financials = calculateFinancials();
  // Get status badge color
  const getStatusBadgeColor = () => {
    switch (dealStatus) {
      case DEAL_STATUS.ANALYSIS:
        return 'bg-blue-100 text-blue-800';
      case DEAL_STATUS.PASSED:
        return 'bg-red-100 text-red-800';
      case DEAL_STATUS.OFFER:
        return 'bg-yellow-100 text-yellow-800';
      case DEAL_STATUS.ESCROW:
        return 'bg-purple-100 text-purple-800';
      case DEAL_STATUS.PORTFOLIO:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status text
  const getStatusText = () => {
    switch (dealStatus) {
      case DEAL_STATUS.ANALYSIS:
        return 'Under Analysis';
      case DEAL_STATUS.PASSED:
        return 'Passed On';
      case DEAL_STATUS.OFFER:
        return 'Offer Written';
      case DEAL_STATUS.ESCROW:
        return 'In Escrow';
      case DEAL_STATUS.PORTFOLIO:
        return 'In Portfolio';
      default:
        return 'Unknown';
    }
  };
  // Get status icon
  const getStatusIcon = () => {
    switch (dealStatus) {
      case DEAL_STATUS.ANALYSIS:
        return <CalculatorIcon className="w-4 h-4 mr-1" data-id="element-315" />;
      case DEAL_STATUS.PASSED:
        return <ThumbsDownIcon className="w-4 h-4 mr-1" data-id="element-316" />;
      case DEAL_STATUS.OFFER:
        return <FileTextIcon className="w-4 h-4 mr-1" data-id="element-317" />;
      case DEAL_STATUS.ESCROW:
        return <KeyIcon className="w-4 h-4 mr-1" data-id="element-318" />;
      case DEAL_STATUS.PORTFOLIO:
        return <HomeIconSolid className="w-4 h-4 mr-1" data-id="element-319" />;
      default:
        return <AlertTriangleIcon className="w-4 h-4 mr-1" data-id="element-320" />;
    }
  };
  // Save deal to history
  const saveDealToHistory = () => {
    if (!propertyData) return;
    const dealToSave = {
      id: `deal${Date.now()}`,
      address: propertyData.address,
      dateAdded: new Date().toISOString().split('T')[0],
      status: dealStatus,
      purchasePrice: parseFloat(purchasePrice) || 0,
      arv: parseFloat(afterRepairValue) || 0,
      constructionCost: parseFloat(constructionCost) || 0,
      roi: financials.roi,
      notes: selectedRecommendation ? selectedRecommendation.description : '',
      imageUrl: propertyData.imageUrl,
      category: 'Single Family',
      zillowEstimate: propertyData.zillowEstimate,
      redfintEstimate: propertyData.redfintEstimate
    };
    // Get existing deals from localStorage
    const savedDeals = localStorage.getItem('dealHistory');
    let deals = [];
    if (savedDeals) {
      deals = JSON.parse(savedDeals);
    }
    // Add new deal
    deals.push(dealToSave);
    // Save back to localStorage
    localStorage.setItem('dealHistory', JSON.stringify(deals));
    // Update UI
    setSavedToHistory(true);
    // Optionally, navigate to the history page
    setTimeout(() => {
      navigate('/deal-history');
    }, 1500);
  };
  return <div className="max-w-7xl mx-auto" data-id="element-321">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-322">Deal Analysis</h1>
      {/* Property Search Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6" data-id="element-323">
        <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-324">
          Property Search
        </h2>
        <div className="flex flex-col md:flex-row gap-4" data-id="element-325">
          <div className="relative flex-grow" data-id="element-326">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-327">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-328" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter property address..." value={address} onChange={e => setAddress(e.target.value)} data-id="element-329" />
          </div>
          <Button onClick={handlePropertySearch} disabled={isLoading || !address.trim()} data-id="element-330">
            {isLoading ? 'Searching...' : 'Search Property'}
          </Button>
          {/* Add link to Deal History */}
          <Link to="/deal-history" data-id="element-331">
            <Button variant="outline" data-id="element-332">
              <HistoryIcon className="w-4 h-4 mr-2" data-id="element-333" />
              View Deal History
            </Button>
          </Link>
        </div>
      </div>
      {/* Property Details Section - Show after search */}
      {propertyData && <div className="space-y-6" data-id="element-334">
          {/* Deal Status Bar */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4" data-id="element-335">
            <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-336">
              <div className="flex items-center mb-4 md:mb-0" data-id="element-337">
                <span className="text-gray-700 font-medium mr-3" data-id="element-338">
                  Deal Status:
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor()}`} data-id="element-339">
                  {getStatusIcon()}
                  {getStatusText()}
                </span>
                {dealStatus === DEAL_STATUS.PASSED && rejectionReason && <span className="ml-3 text-sm text-gray-500" data-id="element-340">
                    Reason: {rejectionReason}
                  </span>}
              </div>
              <div className="flex space-x-3" data-id="element-341">
                {dealStatus === DEAL_STATUS.ANALYSIS && <>
                    <Button variant="outline" size="sm" onClick={handleRejectDeal} className="border-red-300 text-red-700 hover:bg-red-50" data-id="element-342">
                      <ThumbsDownIcon className="w-4 h-4 mr-2" data-id="element-343" />
                      Reject Deal
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleAcceptDeal} className="border-green-300 text-green-700 hover:bg-green-50" data-id="element-344">
                      <ThumbsUpIcon className="w-4 h-4 mr-2" data-id="element-345" />
                      Accept Deal
                    </Button>
                  </>}
                {dealStatus === DEAL_STATUS.OFFER && <Button variant="outline" size="sm" onClick={handleMoveToEscrow} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-id="element-346">
                    <KeyIcon className="w-4 h-4 mr-2" data-id="element-347" />
                    Move to Escrow
                  </Button>}
                {dealStatus === DEAL_STATUS.ESCROW && <Button variant="outline" size="sm" onClick={handleMoveToPortfolio} className="border-green-300 text-green-700 hover:bg-green-50" data-id="element-348">
                    <HomeIconSolid className="w-4 h-4 mr-2" data-id="element-349" />
                    Close Escrow
                  </Button>}
                {(dealStatus === DEAL_STATUS.PASSED || dealStatus === DEAL_STATUS.PORTFOLIO) && <Button variant="outline" size="sm" onClick={resetDealStatus} data-id="element-350">
                    <ArrowRightIcon className="w-4 h-4 mr-2" data-id="element-351" />
                    Reset Status
                  </Button>}
              </div>
            </div>
          </div>
          {/* Property Details */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-352">
            <div className="p-6" data-id="element-353">
              <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-354">
                Property Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-355">
                <div className="md:col-span-1" data-id="element-356">
                  <img src={propertyData.imageUrl} alt="Property" className="w-full h-48 object-cover rounded-lg" data-id="element-357" />
                </div>
                <div className="md:col-span-2" data-id="element-358">
                  <div className="flex justify-between items-start mb-2" data-id="element-359">
                    <h3 className="font-medium text-gray-900" data-id="element-360">
                      {propertyData.address}
                    </h3>
                    <div className="flex space-x-2" data-id="element-361">
                      <a href={getZillowUrl(propertyData.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-362">
                        <ExternalLinkIcon className="w-4 h-4 mr-1" data-id="element-363" />
                        Zillow
                      </a>
                      <a href={getRedfinUrl(propertyData.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm text-red-600 hover:text-red-800" data-id="element-364">
                        <ExternalLinkIcon className="w-4 h-4 mr-1" data-id="element-365" />
                        Redfin
                      </a>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-366">
                    <div data-id="element-367">
                      <p className="text-sm text-gray-500" data-id="element-368">Bedrooms</p>
                      <p className="font-medium" data-id="element-369">{propertyData.bedrooms}</p>
                    </div>
                    <div data-id="element-370">
                      <p className="text-sm text-gray-500" data-id="element-371">Bathrooms</p>
                      <p className="font-medium" data-id="element-372">{propertyData.bathrooms}</p>
                    </div>
                    <div data-id="element-373">
                      <p className="text-sm text-gray-500" data-id="element-374">Structure Size</p>
                      <p className="font-medium" data-id="element-375">
                        {propertyData.structureSize.toLocaleString()} sq ft
                      </p>
                    </div>
                    <div data-id="element-376">
                      <p className="text-sm text-gray-500" data-id="element-377">Lot Size</p>
                      <p className="font-medium" data-id="element-378">
                        {propertyData.propertySize.toLocaleString()} sq ft
                      </p>
                    </div>
                    <div data-id="element-379">
                      <p className="text-sm text-gray-500" data-id="element-380">Year Built</p>
                      <p className="font-medium" data-id="element-381">{propertyData.yearBuilt}</p>
                    </div>
                    <div data-id="element-382">
                      <p className="text-sm text-gray-500" data-id="element-383">Condition</p>
                      <p className="font-medium" data-id="element-384">
                        {propertyData.currentCondition}
                      </p>
                    </div>
                  </div>
                  {/* Property Valuation Section */}
                  <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-385">
                    <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-386">
                      Property Valuation
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-387">
                      <div className={`border rounded-lg p-3 ${valuationSource === 'manual' ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`} data-id="element-388">
                        <div className="flex justify-between items-center mb-1" data-id="element-389">
                          <span className="text-sm text-gray-500" data-id="element-390">
                            Our Estimate
                          </span>
                          <input type="radio" name="valuationSource" checked={valuationSource === 'manual'} onChange={() => handleValuationSourceChange('manual')} data-id="element-391" />
                        </div>
                        <p className="text-lg font-semibold text-gray-900" data-id="element-392">
                          ${propertyData.estimatedValue.toLocaleString()}
                        </p>
                      </div>
                      <div className={`border rounded-lg p-3 ${valuationSource === 'zillow' ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`} data-id="element-393">
                        <div className="flex justify-between items-center mb-1" data-id="element-394">
                          <div className="flex items-center" data-id="element-395">
                            <span className="text-sm text-gray-500" data-id="element-396">
                              Zillow Zestimate
                            </span>
                          </div>
                          <input type="radio" name="valuationSource" checked={valuationSource === 'zillow'} onChange={() => handleValuationSourceChange('zillow')} data-id="element-397" />
                        </div>
                        <p className="text-lg font-semibold text-gray-900" data-id="element-398">
                          ${propertyData.zillowEstimate.toLocaleString()}
                        </p>
                      </div>
                      <div className={`border rounded-lg p-3 ${valuationSource === 'redfin' ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`} data-id="element-399">
                        <div className="flex justify-between items-center mb-1" data-id="element-400">
                          <div className="flex items-center" data-id="element-401">
                            <span className="text-sm text-gray-500" data-id="element-402">
                              Redfin Estimate
                            </span>
                          </div>
                          <input type="radio" name="valuationSource" checked={valuationSource === 'redfin'} onChange={() => handleValuationSourceChange('redfin')} data-id="element-403" />
                        </div>
                        <p className="text-lg font-semibold text-gray-900" data-id="element-404">
                          ${propertyData.redfintEstimate.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Comparable Properties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-405">
            {/* Current Condition Comps */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-406">
              <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-407">
                Comparable Properties (Current Condition)
              </h2>
              <div className="space-y-4" data-id="element-408">
                {mockCompsCurrentCondition.map((comp, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-409">
                    <p className="font-medium text-gray-900 mb-1" data-id="element-410">
                      {comp.address}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-2" data-id="element-411">
                      <div data-id="element-412">
                        <span className="text-gray-500" data-id="element-413">Beds/Baths: </span>
                        <span data-id="element-414">
                          {comp.bedrooms}/{comp.bathrooms}
                        </span>
                      </div>
                      <div data-id="element-415">
                        <span className="text-gray-500" data-id="element-416">Size: </span>
                        <span data-id="element-417">{comp.size} sq ft</span>
                      </div>
                      <div data-id="element-418">
                        <span className="text-gray-500" data-id="element-419">Sold: </span>
                        <span data-id="element-420">{comp.saleDate}</span>
                      </div>
                      <div data-id="element-421">
                        <span className="text-gray-500" data-id="element-422">Distance: </span>
                        <span data-id="element-423">{comp.distanceInMiles} mi</span>
                      </div>
                    </div>
                    <div className="font-semibold text-green-600" data-id="element-424">
                      ${comp.salePrice.toLocaleString()}
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Best Use Comps */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-425">
              <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-426">
                Comparable Properties (After Improvement)
              </h2>
              <div className="space-y-4" data-id="element-427">
                {mockCompsBestUse.map((comp, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-428">
                    <p className="font-medium text-gray-900 mb-1" data-id="element-429">
                      {comp.address}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-2" data-id="element-430">
                      <div data-id="element-431">
                        <span className="text-gray-500" data-id="element-432">Beds/Baths: </span>
                        <span data-id="element-433">
                          {comp.bedrooms}/{comp.bathrooms}
                        </span>
                      </div>
                      <div data-id="element-434">
                        <span className="text-gray-500" data-id="element-435">Size: </span>
                        <span data-id="element-436">{comp.size} sq ft</span>
                      </div>
                      <div data-id="element-437">
                        <span className="text-gray-500" data-id="element-438">Sold: </span>
                        <span data-id="element-439">{comp.saleDate}</span>
                      </div>
                      <div data-id="element-440">
                        <span className="text-gray-500" data-id="element-441">Distance: </span>
                        <span data-id="element-442">{comp.distanceInMiles} mi</span>
                      </div>
                    </div>
                    <div className="font-semibold text-green-600" data-id="element-443">
                      ${comp.salePrice.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1" data-id="element-444">
                      {comp.notes}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Recommendations */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-445">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-446">
              Recommended Strategies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-447">
              {mockRecommendations.map((recommendation, index) => <div key={index} className={`border rounded-lg p-4 cursor-pointer transition-colors ${selectedRecommendation === recommendation ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => selectRecommendation(recommendation)} data-id="element-448">
                  <div className="flex justify-between items-start mb-2" data-id="element-449">
                    <h3 className="font-medium text-gray-900" data-id="element-450">
                      {recommendation.type}
                    </h3>
                    {selectedRecommendation === recommendation && <CheckCircleIcon className="w-5 h-5 text-blue-500" data-id="element-451" />}
                  </div>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-452">
                    {recommendation.description}
                  </p>
                  <div className="space-y-1 text-sm" data-id="element-453">
                    <div className="flex justify-between" data-id="element-454">
                      <span className="text-gray-500" data-id="element-455">Est. Cost:</span>
                      <span className="font-medium" data-id="element-456">
                        ${recommendation.estimatedCost.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-457">
                      <span className="text-gray-500" data-id="element-458">Est. Value:</span>
                      <span className="font-medium" data-id="element-459">
                        ${recommendation.estimatedValue.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-460">
                      <span className="text-gray-500" data-id="element-461">ROI:</span>
                      <span className="font-medium text-green-600" data-id="element-462">
                        {recommendation.estimatedROI.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-463">
                      <span className="text-gray-500" data-id="element-464">Timeframe:</span>
                      <span className="font-medium" data-id="element-465">
                        {recommendation.timeframe}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Financial Analysis */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-466">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-467">
              Financial Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-468">
              <div data-id="element-469">
                <h3 className="font-medium text-gray-700 mb-3" data-id="element-470">Deal Inputs</h3>
                <div className="space-y-4" data-id="element-471">
                  <div data-id="element-472">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-473">
                      Purchase Price
                    </label>
                    <div className="relative rounded-md shadow-sm" data-id="element-474">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-475">
                        <span className="text-gray-500 sm:text-sm" data-id="element-476">$</span>
                      </div>
                      <input type="text" className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value.replace(/[^0-9]/g, ''))} data-id="element-477" />
                    </div>
                  </div>
                  <div data-id="element-478">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-479">
                      Construction Cost
                    </label>
                    <div className="relative rounded-md shadow-sm" data-id="element-480">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-481">
                        <span className="text-gray-500 sm:text-sm" data-id="element-482">$</span>
                      </div>
                      <input type="text" className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" value={constructionCost} onChange={e => setConstructionCost(e.target.value.replace(/[^0-9]/g, ''))} data-id="element-483" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-484">
                    <div data-id="element-485">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-486">
                        Holding Period (months)
                      </label>
                      <input type="number" min="1" max="36" className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md" value={holdingPeriod} onChange={e => setHoldingPeriod(e.target.value)} data-id="element-487" />
                    </div>
                    <div data-id="element-488">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-489">
                        Monthly Holding Cost
                      </label>
                      <div className="relative rounded-md shadow-sm" data-id="element-490">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-491">
                          <span className="text-gray-500 sm:text-sm" data-id="element-492">$</span>
                        </div>
                        <input type="text" className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" value={holdingCostPerMonth} onChange={e => setHoldingCostPerMonth(e.target.value.replace(/[^0-9]/g, ''))} data-id="element-493" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-494">
                    <div data-id="element-495">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-496">
                        Sales Commission (%)
                      </label>
                      <input type="number" min="0" max="10" step="0.1" className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md" value={salesCommissionPercent} onChange={e => setSalesCommissionPercent(e.target.value)} data-id="element-497" />
                    </div>
                    <div data-id="element-498">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-499">
                        After Repair Value
                      </label>
                      <div className="relative rounded-md shadow-sm" data-id="element-500">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-501">
                          <span className="text-gray-500 sm:text-sm" data-id="element-502">$</span>
                        </div>
                        <input type="text" className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" value={afterRepairValue} onChange={e => setAfterRepairValue(e.target.value.replace(/[^0-9]/g, ''))} data-id="element-503" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-504">
                <h3 className="font-medium text-gray-700 mb-3" data-id="element-505">Results</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-506">
                  <div className="space-y-4" data-id="element-507">
                    <div data-id="element-508">
                      <div className="flex justify-between items-center mb-1" data-id="element-509">
                        <span className="text-gray-600" data-id="element-510">Purchase Price:</span>
                        <span className="font-medium" data-id="element-511">
                          ${parseInt(purchasePrice || '0').toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-1" data-id="element-512">
                        <span className="text-gray-600" data-id="element-513">
                          Construction Cost:
                        </span>
                        <span className="font-medium" data-id="element-514">
                          ${parseInt(constructionCost || '0').toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-1" data-id="element-515">
                        <span className="text-gray-600" data-id="element-516">Holding Costs:</span>
                        <span className="font-medium" data-id="element-517">
                          $
                          {(parseInt(holdingCostPerMonth || '0') * parseInt(holdingPeriod || '0')).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-1" data-id="element-518">
                        <span className="text-gray-600" data-id="element-519">Sales Commission:</span>
                        <span className="font-medium" data-id="element-520">
                          $
                          {(parseInt(afterRepairValue || '0') * parseFloat(salesCommissionPercent || '0') / 100).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200" data-id="element-521">
                        <span className="font-medium text-gray-700" data-id="element-522">
                          Total Investment:
                        </span>
                        <span className="font-semibold" data-id="element-523">
                          $
                          {Math.round(financials.totalInvestment).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-200" data-id="element-524">
                      <div className="flex justify-between items-center mb-1" data-id="element-525">
                        <span className="text-gray-600" data-id="element-526">
                          After Repair Value:
                        </span>
                        <span className="font-medium" data-id="element-527">
                          ${parseInt(afterRepairValue || '0').toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-1" data-id="element-528">
                        <span className="text-gray-600" data-id="element-529">Gross Profit:</span>
                        <span className={`font-medium ${financials.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-530">
                          ${Math.round(financials.grossProfit).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200" data-id="element-531">
                        <span className="font-medium text-gray-700" data-id="element-532">
                          Net Profit:
                        </span>
                        <span className={`font-semibold ${financials.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-533">
                          ${Math.round(financials.netProfit).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-1" data-id="element-534">
                        <span className="font-medium text-gray-700" data-id="element-535">
                          Return on Investment:
                        </span>
                        <span className={`font-semibold ${financials.roi >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-536">
                          {financials.roi.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-200" data-id="element-537">
                      <div className="flex justify-center" data-id="element-538">
                        {savedToHistory ? <div className="flex items-center text-green-600" data-id="element-539">
                            <CheckCircleIcon className="w-5 h-5 mr-2" data-id="element-540" />
                            Saved to Deal History
                          </div> : <Button className="w-full" onClick={saveDealToHistory} data-id="element-541">
                            <FileTextIcon className="w-4 h-4 mr-2" data-id="element-542" />
                            Save Deal Analysis
                          </Button>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Rejection Reason Modal */}
      {showRejectionModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-543">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4" data-id="element-544">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-545">
              Why are you rejecting this deal?
            </h3>
            <textarea className="w-full border border-gray-300 rounded-md p-2 mb-4 h-32 focus:ring-blue-500 focus:border-blue-500" placeholder="Enter reason for rejection..." value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} data-id="element-546"></textarea>
            <div className="flex justify-end space-x-3" data-id="element-547">
              <Button variant="outline" onClick={() => setShowRejectionModal(false)} data-id="element-548">
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmRejectDeal} data-id="element-549">
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};
export default DealInput;